/**
 * Client for the Go-Live deployment API (/api/golive/*).
 *
 * Reads are open; writes require an editor session (cookie set by login).
 * Every mutation returns the updated deployment, so callers just setState from
 * the response — no separate refetch needed.
 */
import type { GoLiveChecklist } from "@/types/golive"

export interface Progress {
  total: number
  done: number
  pct: number
}

export interface DeploymentSummary {
  id: number
  version: string
  rel_type: "major" | "minor" | "hotfix"
  status: "active" | "completed"
  locked: boolean
  outcome: "deployed" | "rolled_back" | null
  title: string
  deploy_date: string | null
  progress: Progress
  created_at: string
  created_by: string
  completed_at: string | null
  updated_at: string
}

export interface Deployment extends DeploymentSummary {
  doc: GoLiveChecklist
  completion_notes: string | null
}

export interface Session {
  authRequired: boolean
  /** When true the whole site is private — a valid code is required just to view. */
  viewLocked?: boolean
  user: string | null
  username?: string | null
  /** Dashboard-tab allowlist for this person (null/absent = all tabs). */
  tabs?: string[] | null
}

export interface AuditRow {
  actor: string
  at: string
  action: string
  detail: Record<string, unknown> | null
}

export interface Person {
  name: string
  role: string
}

const BASE = "/api/golive"
const TOKEN_KEY = "golive-token"

// The session token is kept in localStorage and sent as a Bearer header, not
// just relied on via the cookie — so editing works inside the ADO iframe, where
// third-party cookies are commonly blocked but localStorage is not.
const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}
const setToken = (t: string | null) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) }
  if (init?.body) headers["Content-Type"] = "application/json"
  if (token) headers["Authorization"] = `Bearer ${token}`
  const res = await fetch(`${BASE}${path}`, { credentials: "include", ...init, headers })
  if (!res.ok) {
    let detail = res.statusText
    try {
      detail = (await res.json()).detail ?? detail
    } catch {
      /* ignore */
    }
    const err = new Error(detail) as Error & { status: number }
    err.status = res.status
    throw err
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

const body = (data: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(data) })

export const golive = {
  whoami: () => req<Session>("/whoami"),
  login: async (username: string, password: string) => {
    const r = await req<{ name: string; username: string; token: string }>(
      "/login",
      body({ username, password }),
    )
    setToken(r.token)
    return r
  },
  unlock: async (code: string) => {
    const r = await req<{ name: string; username: string; token: string }>(
      "/unlock",
      body({ code }),
    )
    setToken(r.token)
    return r
  },
  logout: async () => {
    setToken(null)
    return req<{ ok: boolean }>("/logout", { method: "POST" })
  },

  people: () => req<Person[]>("/people"),
  list: () => req<DeploymentSummary[]>("/deployments"),
  get: (id: number) => req<Deployment>(`/deployments/${id}`),
  create: (data: { version: string; deployDate?: string | null; relType?: string | null }) =>
    req<Deployment>("/deployments", body(data)),

  rename: (id: number, version: string) =>
    req<Deployment>(`/deployments/${id}/version`, { method: "PATCH", body: JSON.stringify({ version }) }),
  patchHeader: (id: number, key: string, value: string) =>
    req<Deployment>(`/deployments/${id}/header`, { method: "PATCH", body: JSON.stringify({ key, value }) }),
  addItem: (id: number, data: Record<string, unknown>) =>
    req<Deployment>(`/deployments/${id}/items`, body(data)),
  patchItem: (id: number, itemId: string | number, patch: Record<string, unknown>) =>
    req<Deployment>(`/deployments/${id}/items/${itemId}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteItem: (id: number, itemId: string | number) =>
    req<Deployment>(`/deployments/${id}/items/${itemId}`, { method: "DELETE" }),
  reorder: (id: number, phaseId: string, orderedIds: (string | number)[]) =>
    req<Deployment>(`/deployments/${id}/reorder`, body({ phaseId, orderedIds })),
  complete: (id: number, notes: string, kind: "deployment" | "rollback" = "deployment") =>
    req<Deployment>(`/deployments/${id}/complete`, body({ notes, kind })),
  reopen: (id: number) => req<Deployment>(`/deployments/${id}/reopen`, { method: "POST" }),
  remove: (id: number) => req<{ ok: boolean }>(`/deployments/${id}`, { method: "DELETE" }),
  audit: (id: number) => req<AuditRow[]>(`/deployments/${id}/audit`),
}
